import {Reducer, updateFactory} from '@modifi/redux'
import {
  aUserFeedBackSubmitSuccessful,
  TRADE_PARTNER_EXPECTED_FINANCING_LIMIT,
  TRADE_PARTNER_TRADING_CURRENCY,
} from '@modifi/plugin-onboarding'
import {aPush} from '@modifi/router'
import {getAuthenticatedThemisClient} from '@modifi/onboarding-api-clients'
import {isEmpty} from 'ramda'
import {aCloseDialog, aShowDialog} from '@modifi/plugin-dialogs'

import {getLogger} from '../../../../../utils'
import {ROUTE_INTRO} from '../../../../../routes/constants'
import {
  aFetchSellerTradePartners,
  aUpdateLocalSellerTradePartner,
  selectSellerTradePartnerByExternalId,
} from '../../../../../data/seller-trade-partners'
import {aFetchCompany} from '../../../../../data/company'
import {mapTradePartnerToFormData} from '../../../../seller-trade-partners/utils'
import {
  PRE_UNDERWRITING_BUYER_DETAILS,
  SECTION_PRE_UNDERWRITING_BUYER_DETAILS,
} from '../../constants'
import {aHideBuyerForm} from './view'
import {aFetchInputFields} from '../../../../../data/input-fields'
import {aAddLoadingLimitForBuyer} from './loadingLimits'
import {EXPECTED_FINANCING_LIMIT_CAP} from '../../../../seller-trade-partners/constants'
import {TRADE_DOCUMENTS_UPLOAD_DIALOG} from '../../dialogs'

const logger = getLogger('pre-underwriting-buyer-details-page')

const apiClient = getAuthenticatedThemisClient()

export const selectInitialFormData = (
  state: Record<string, any>,
  tradePartnerId?: string
): {initialValues: Record<string, any>; defaultValues: Record<string, any>} => {
  const tradePartner = tradePartnerId
    ? selectSellerTradePartnerByExternalId(state, tradePartnerId)
    : undefined

  const initialValues: Record<string, any> = {}

  if (tradePartner) {
    Object.assign(
      initialValues,
      mapTradePartnerToFormData({
        ...tradePartner,
        ...tradePartner?.tradePartnerCompany,
        ...tradePartner?.tradePartnerCompany?.address,
      })
    )
  }

  return {
    initialValues,
    defaultValues: {},
  }
}

const {actions, selectors, reducer} = updateFactory({
  actionNamePrefix: 'pages/buyer-details-form/submit',
  rootSelector: state => state?.pages?.preUnderwriting?.buyerDetails?.form,
  submitRequest:
    (a, changes: Record<string, any>, externalTradePartnerId: string | undefined) =>
    async (dispatch, getState): Promise<void> => {
      logger.info('Submitting page for buyer in preUnderwriting=%s', externalTradePartnerId)
      logger.debug('changes: %o', changes)

      dispatch(a.pendingAction())

      let isNewBuyer = false
      // the limit and its currency are two separate form fields, but are sent as a single
      // amount object - so both have to be sent even when only one of them was changed
      const existingTradePartner = externalTradePartnerId
        ? selectSellerTradePartnerByExternalId(getState(), externalTradePartnerId)
        : undefined
      // on creation the changes are already mapped into the request shape by the form
      const expectedFinancingLimit = {
        amount:
          changes[TRADE_PARTNER_EXPECTED_FINANCING_LIMIT] ??
          changes.expectedFinancingLimit?.amount ??
          existingTradePartner?.expectedFinancingLimit?.amount,
        currency:
          changes[TRADE_PARTNER_TRADING_CURRENCY] ??
          changes.expectedFinancingLimit?.currency ??
          existingTradePartner?.expectedFinancingLimit?.currency,
      }

      try {
        if (isEmpty(changes)) {
          logger.info(`No company related changes detected - skipping`)
        } else {
          if (!externalTradePartnerId) {
            logger.info(`Creating new buyer...`)
            isNewBuyer = true
            // eslint-disable-next-line no-param-reassign
            externalTradePartnerId = await apiClient.TradePartners.create({
              body: changes,
            })
              ?.then(res => res?.data())
              .then(tradePartner => tradePartner?.tradePartnerCompany?.externalCompanyId)
          } else {
            logger.info(`Updating existing buyer...`)
          }

          // optimistic update
          dispatch(
            aUpdateLocalSellerTradePartner({
              company: {
                ...(changes?.company || null),
                externalCompanyId: externalTradePartnerId,
              },
              expectedFinancingLimit,
            })
          )

          if (!isNewBuyer && externalTradePartnerId) {
            const updatedTradePartner = await apiClient.TradePartners.update({
              externalBuyerTradePartnerId: externalTradePartnerId,
              body: {expectedFinancingLimit},
            }).then(res => res.data())

            dispatch(
              aUpdateLocalSellerTradePartner(
                {
                  ...updatedTradePartner,
                  company: {
                    ...(updatedTradePartner.company || null),
                    externalCompanyId: externalTradePartnerId,
                  },
                },
                false
              )
            )
          }
        }
      } catch (e: any) {
        dispatch(aCloseDialog())
        logger.error(`Error occurred: %o`, e)
        dispatch(a.errorAction(e))
        return
      }

      dispatch(a.successAction())

      dispatch(aCloseDialog())
      await dispatch(aFetchSellerTradePartners())
      await dispatch(aFetchInputFields())
      await dispatch(aFetchCompany())
      isNewBuyer && dispatch(aAddLoadingLimitForBuyer(externalTradePartnerId))
      dispatch(aUserFeedBackSubmitSuccessful(SECTION_PRE_UNDERWRITING_BUYER_DETAILS))

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'preonboarding_buyer_creation_submit',
      })

      if ((expectedFinancingLimit.amount || 0) > EXPECTED_FINANCING_LIMIT_CAP.high) {
        dispatch(aShowDialog(TRADE_DOCUMENTS_UPLOAD_DIALOG, {externalTradePartnerId}))
      } else {
        dispatch(aPush(ROUTE_INTRO, {page: PRE_UNDERWRITING_BUYER_DETAILS}))
        dispatch(aHideBuyerForm())
      }
    },
})

export const {aSubmit: aSavePage, aReset} = actions

export default reducer as Reducer

export const {
  selectUpdatePending: selectSavePending,
  selectUpdateHasError: selectSaveHasError,
  selectUpdateErrorMessage: selectSaveErrorMessage,
} = selectors
